import { useEffect, useRef, useState } from 'react';
import {
  Card,
  CardContent,
  Button,
  Box,
  List,
  ListItem,
  ListItemText,
  Typography,
  Checkbox,
  ListItemButton,
  Paper,
  Divider,
  Grid,
  Menu,
  MenuItem,
  ListItemSecondaryAction,
  IconButton,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { tooltipClasses } from '@mui/material/Tooltip';
import {
  RadialBarChart,
  RadialBar,
  ResponsiveContainer,
  PolarAngleAxis,
} from 'recharts';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import EditOffIcon from '@mui/icons-material/EditOff';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { IconGradientDefinition } from '../../utils/iaDesign'
import { sortTasks } from '../../utils/methods';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { getIAStyles } from '../../utils/iaDesign';

// Redux wrappers
import AddTaskDialog from '../../redux/containers/project/AddTaskDialog';
import EditScopeDialog from '../../redux/containers/project/EditScopeDialog';
import EditTaskDialog from '../../redux/containers/project/EditTaskDialog'

// My components
import DeleteDialog from '../DeleteDialog';

const BootstrapTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
  },
}));

export default function Scope(props) {
  const {
    scope,
    role,
    loadingUpdate,
    updateTask,
    getScopes,
    deleteScope,
    created_by,
    users,
    deleteTask,
    getProjects,
    getRole,
    project,
    onDiscard,
    onLocalScopeUpdate,
    fetchTaskSuggestions,
    saveSuggestedTasks,
    suggestedTasks,
    isLoadingTaskSuggestions,
    isScopesSuggestionsLoading,
    isSuggesting,
    onSuggest,
    onSuggestComplete,
    suggestedForScopeId,
  } = props;
  const {
    tasks_ms,
    tasks_ns,
    name,
    id,
    completed_ns,
    completed_ms,
    total_tasks,
    is_suggestion,
  } = scope;
  const [open, setOpen] = useState(false);
  const [openEditTask, setOpenEditTask] = useState(false);
  const [checked, setChecked] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openEditScopeDialog, setOpenEditScopeDialog] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [openDeleteTask, setOpenDeleteTask] = useState(false);
  const [task, setTask] = useState({
    id: -1,
    order: '',
    description: '',
    is_completed: '',
    is_nice_to_have: '',
    task_name: '',
  });
  const [toDelete, setToDelete] = useState(-1);
  const [localSuggestions, setLocalSuggestions] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const openMenu = Boolean(anchorEl);
  const color = scope.color + '0F';
  const admin = getRole.admin;
  const user_id = getRole.user_id;
  const project_id = project.id;

  const didmountRef = useRef(false);

  var taskLists = {
    must_have: tasks_ms,
    nice_to_have: tasks_ns,
    completed_must_have: 0,
  };
  const id2List = {
    droppable: 'must_have',
    droppable2: 'nice_to_have',
  };

  taskLists = {
    must_have: sortTasks(tasks_ms, 'must_have'),
    nice_to_have: sortTasks(tasks_ns, 'nice_to_have'),
    completed_must_have: tasks_ms.length,
  };

  var ns_prop = (completed_ns / tasks_ns.length) * 100;
  var ms_prop = (completed_ms / tasks_ms.length) * 100;

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpen = () => {
    setOpen(!open);
  };

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseTaskDialog = () => {
    handleClose();
    setOpenEditTask(!openEditTask);
  };

  useEffect(() => {
    if (didmountRef.current && task.id !== -1) {
      return handleCloseTaskDialog();
    }
    didmountRef.current = true;
  }, [task])

  const handleCloseScopeDialog = () => {
    handleClose();
    setOpenEditScopeDialog(!openEditScopeDialog);
  };

  const handleLocalEditScopeSave = (data) => {
    if (onLocalScopeUpdate) {
      onLocalScopeUpdate(id, data);
    }
    setOpenEditScopeDialog(false);
  };

  // Effect to load suggestions into local state
  useEffect(() => {
    if (suggestedTasks && suggestedTasks.length > 0 && isSuggesting && suggestedForScopeId === id) {
      const newSuggestions = suggestedTasks.map((t, i) => {
        let name = typeof t === 'string' ? t : t.name;
        let is_nice_to_have = typeof t === 'object' ? !!t.is_nice_to_have : false;

        if (name && name.trim().startsWith('~')) {
          is_nice_to_have = true;
          name = name.replace('~', '').trim();
        }

        return {
          id: `suggestion-${Date.now()}-${i}`,
          name,
          description: typeof t === 'object' ? t.description : '',
          is_nice_to_have,
          is_completed: false,
          is_suggestion: true,
          order: 0
        };
      });
      setLocalSuggestions(newSuggestions);
    }
  }, [suggestedTasks, suggestedForScopeId, isSuggesting, id]);

  const handleSuggest = () => {
    if (project.pitch_link) {
      onSuggest();
      fetchTaskSuggestions({ file_url: project.pitch_link, scope_id: id, project_id: project.id });
    }
  };

  const handleLocalSuggestionSave = (updatedTask) => {
    setLocalSuggestions(prev => prev.map(t => t.id === updatedTask.id ? { ...t, ...updatedTask } : t));
    setOpenEditTask(false);
  };

  const handleSaveSuggestions = async () => {
    setIsSaving(true);
    try {
      await saveSuggestedTasks({
        scope_id: id,
        project_id: project.id,
        created_by: created_by,
        tasks: localSuggestions.map(t => ({
          name: t.name,
          description: t.description,
          is_nice_to_have: t.is_nice_to_have
        })),
      });
      await getScopes(project.id);
      onSuggestComplete();
      setLocalSuggestions([]);
    } catch (error) {
      console.error('Error saving suggestions:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async tasksData => {
    // Check if the scope is an AI suggestion
    if (is_suggestion) {
      // Reconstruct the task object from the array sent by the checkbox logic
      const updatedTaskObj = {
        id: tasksData[0],
        order: tasksData[1],
        description: tasksData[2],
        is_completed: tasksData[3],
        is_nice_to_have: tasksData[4],
        name: tasksData[5]
      };

      // Call the parent's local update function
      if (props.onLocalUpdate) {
        props.onLocalUpdate(id, updatedTaskObj);
      }
      return; // Stop execution here, do not call API
    }
    loadingUpdate(true);
    await updateTask({ tasks: tasksData });
    await getScopes(project_id);
    loadingUpdate(false);
  };

  const handleDelete = async () => {
    handleClose();
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteTask = () => {
    loadingUpdate(true);
    handleClose();
    setOpenDeleteTask(!openDeleteTask);
    loadingUpdate(false);
  };

  const handleDeleteTask = async task_id => {
    // Local delete for suggestions (both scope suggestions and task suggestions)
    if (is_suggestion || (typeof task_id === 'string' && task_id.startsWith('suggestion-'))) {
      if (!is_suggestion) setLocalSuggestions(prev => prev.filter(t => t.id !== task_id));
      handleClose();
      setOpenDeleteTask(false);
      if (props.onLocalDelete) props.onLocalDelete(id, task_id);
      return;
    }

    loadingUpdate(true);
    await deleteTask(task_id);
    await getScopes(project_id);
    loadingUpdate(false);
  };


  const handleLocalEditTaskSave = (editedTaskData) => {
    props.onLocalUpdate(id, editedTaskData);
    setOpenEditTask(false);
  }

  useEffect(() => {
    if (didmountRef.current && toDelete !== -1) {
      return handleCloseDeleteTask();
    }
    didmountRef.current = true;
  }, [toDelete])

  const handleDeleteMode = () => {
    handleClose();
    setDeleteMode(!deleteMode);
  };

  const handleToggle = value => () => {
    if (value.is_suggestion) {
      setLocalSuggestions(prev => prev.map(t => t.id === value.id ? { ...t, is_completed: !t.is_completed } : t));
      return;
    }

    if (role === 'Member' || admin) {
      const currentIndex = checked.indexOf(value);
      var newChecked = [...checked];
      value.is_completed = !value.is_completed;
      handleUpdate([
        value.id,
        value.order,
        value.description,
        value.is_completed,
        !!value.is_nice_to_have,
        value.name,
        id
      ]);
      if (currentIndex === -1) {
        newChecked.push(value);
      } else {
        newChecked.splice(currentIndex, 1);
      }
      setChecked(newChecked);
    }
  };
  if (tasks_ns.length === 0) {
    ns_prop = 100;
  }

  if (ns_prop === Infinity || isNaN(ns_prop)) {
    ns_prop = 0;
  }

  if (ms_prop === Infinity || isNaN(ms_prop)) {
    ms_prop = 0;
  }
  const data = [
    {
      name: 'nice to have',
      uv: ns_prop,
      fill: '#0070B7',
    },
    {
      name: 'must have',
      uv: ms_prop,
      fill: '#46bf83',
    },
  ];

  const height =
    props.noMargin
      ? '100%'
      : (role === 'Member' || role === 'Project Manager' || role === 'Viewer' || admin)
        ? '600px'
        : '100px';

  const local_ms = localSuggestions.filter(t => !t.is_nice_to_have);
  const local_ns = localSuggestions.filter(t => t.is_nice_to_have);

  return (
    <Paper
      elevation={3}
      sx={{
        margin: '10px',
        position: 'relative',
        ...(is_suggestion ? getIAStyles('card') : {}),
      }}>
      <IconGradientDefinition />

      <Card
        sx={{
          height: height,
          backgroundColor: color,
          position: 'relative',
          ...(is_suggestion ? getIAStyles('cardContent') : {}),
        }}>
        <CardContent>
          <Grid container alignItems="center">
            <Grid item xs={10}>
              {is_suggestion ? (
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{ cursor: 'pointer', color: '#d32f2f' }}>
                  <IconButton
                    onClick={() => onDiscard(id)}
                    sx={{
                      color: '#9C27B0',
                      '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.1)' }
                    }}
                  >
                    <CancelIcon />
                  </IconButton>
                </Box>
              ) : (
                <Typography variant="subtitle2" component="h2">
                  {completed_ms}/{taskLists.completed_must_have} completed
                </Typography>
              )}
            </Grid>
            <Grid item xs={2}>
              <Box display="flex" justifyContent="flex-end" alignItems="center">
                {(role === 'Member' || admin) && (
                  <>
                    {!is_suggestion && (
                      localSuggestions.length > 0 ? (
                        <Tooltip title={isSaving ? 'Saving...' : 'Save tasks'}>
                          <span>
                            <IconButton
                              onClick={handleSaveSuggestions}
                              disabled={isSaving}
                              sx={getIAStyles('iconOnly', isSaving)}
                            >
                              <DoneAllIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Get AI Task Suggestions">
                          <span>
                            <IconButton
                              onClick={handleSuggest}
                              disabled={isLoadingTaskSuggestions || isScopesSuggestionsLoading}
                              sx={{
                                mr: 1,
                                ...(isLoadingTaskSuggestions && isSuggesting
                                  ? getIAStyles('iconOnlyLoading')
                                  : getIAStyles('iconOnly', isLoadingTaskSuggestions || isScopesSuggestionsLoading))
                              }}
                            >
                              <AutoAwesomeIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )
                    )}
                    <Button
                      id="basic-button"
                      aria-controls={open ? 'basic-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={open ? 'true' : undefined}
                      onClick={handleClick}>
                      <MenuIcon fontSize="small" />
                    </Button>
                    {!deleteMode ? (
                      <Menu
                        id="basic-menu"
                        anchorEl={anchorEl}
                        open={openMenu}
                        onClose={handleClose}
                        MenuListProps={{
                          'aria-labelledby': 'basic-button',
                        }}>
                        {!is_suggestion && (
                          <MenuItem
                            onClick={handleOpen}
                            sx={{ backgroundColor: color }}>
                            <AddIcon />
                            Add new task
                          </MenuItem>
                        )}
                        <MenuItem
                          onClick={handleCloseScopeDialog}
                          sx={{ backgroundColor: color }}>
                          <EditIcon />
                          Edit scope
                        </MenuItem>
                        {!is_suggestion && (
                          <MenuItem
                            onClick={handleDeleteMode}
                            sx={{ backgroundColor: color }}>
                            <EditIcon />
                            Update Tasks
                          </MenuItem>
                        )}
                        {!is_suggestion && (
                          <MenuItem
                            onClick={handleDelete}
                            sx={{ backgroundColor: color }}>
                            <DeleteIcon />
                            Delete scope
                          </MenuItem>
                        )}
                      </Menu>
                    ) : (
                      <Menu
                        id="basic-menu"
                        anchorEl={anchorEl}
                        open={openMenu}
                        onClose={handleClose}
                        MenuListProps={{
                          'aria-labelledby': 'basic-button',
                        }}>
                        <MenuItem
                          onClick={handleDeleteMode}
                          sx={{ backgroundColor: color }}>
                          <EditOffIcon />
                          Cancel update
                        </MenuItem>
                      </Menu>
                    )}
                  </>
                )}
              </Box>
            </Grid>

            <Grid item xs={2} sx={{ paddingLeft: '16px', display: 'flex' }}>
              {!is_suggestion && (
                <ResponsiveContainer width={30} height={30}>
                  <RadialBarChart
                    innerRadius={'30%'}
                    outerRadius={'100%'}
                    barSize={4}
                    data={data}
                    margin={'-4em'}
                    startAngle={0}
                    endAngle={360}>
                    <PolarAngleAxis
                      type="number"
                      domain={[0, 100]}
                      dataKey={'uv'}
                      angleAxisId={0}
                      tick={false}
                    />
                    <RadialBar
                      background
                      dataKey="uv"
                      angleAxisId={0}
                      data={data}
                    />
                  </RadialBarChart>
                </ResponsiveContainer>
              )}
            </Grid>
            <Grid item xs={10}>
              <Typography variant="h6" component="h2">
                {name}
              </Typography>
            </Grid>
          </Grid>
          {!(props.noMargin || props.isDragging) &&
            (role === 'Member' ||
              role === 'Project Manager' ||
              role === 'Viewer' ||
              admin) ? (
            <Box>
              <Box margin={1}>
                <Divider> Must have </Divider>
              </Box>
              <Droppable droppableId={`must_have_${id}`}>
                {provided => (
                  <List
                    sx={{ overflow: 'auto', height: '250px' }}
                    ref={provided.innerRef}
                    {...provided.droppableProps}>
                    {(taskLists.must_have.length > 0 || local_ms.length > 0) ? (
                      <>
                        {taskLists.must_have.map((item, index) => {
                          const labelId = `checkbox-list-label-${item.id}`;
                          const dragid = `draggable-${item.id}`;
                          return (
                            <Draggable
                              key={dragid}
                              draggableId={dragid}
                              index={index}
                              isDragDisabled={(role !== 'Member' && !admin) || is_suggestion}>
                              {provided => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}>
                                  <ListItem>
                                    <BootstrapTooltip title={item.description} placement="top">
                                      <ListItemButton
                                        disableRipple
                                        dense
                                        onClick={is_suggestion ? undefined : handleToggle(item)}
                                        sx={{ cursor: is_suggestion ? 'default' : 'pointer' }}>
                                        {!is_suggestion && (
                                          <Checkbox
                                            edge="start"
                                            disableRipple
                                            checked={Boolean(item.is_completed)}
                                            disabled={is_suggestion}
                                            inputProps={{
                                              'aria-labelledby': labelId,
                                            }}
                                          />
                                        )}
                                        <ListItemText primary={item.name} />
                                      </ListItemButton>
                                    </BootstrapTooltip>
                                    {deleteMode || is_suggestion ? (
                                      <ListItemSecondaryAction>
                                        <IconButton
                                          edge='end'
                                          onClick={() => { setTask(item); }}>
                                          <EditIcon />
                                        </IconButton>
                                        <IconButton
                                          edge='end'
                                          onClick={() => is_suggestion
                                            ? handleDeleteTask(item.id)
                                            : setToDelete(item.id)}>
                                          <DeleteIcon />
                                        </IconButton>
                                      </ListItemSecondaryAction>
                                    ) : null}
                                  </ListItem>
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {local_ms.map((item) => (
                          <ListItem key={item.id} sx={getIAStyles('suggestionItem')}>
                            <BootstrapTooltip title={item.description} placement="top">
                              <ListItemButton
                                disableRipple
                                dense
                                sx={{ cursor: 'default' }}>
                                <ListItemText
                                  primary={<span className="ai-suggestion-text">{item.name}</span>}
                                  primaryTypographyProps={{ style: { color: '#9C27B0' } }}
                                />
                              </ListItemButton>
                            </BootstrapTooltip>
                            <ListItemSecondaryAction>
                              <IconButton
                                edge='end'
                                onClick={() => { setTask(item); }}
                                sx={getIAStyles('iconOnly')}>
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                edge='end'
                                onClick={() => handleDeleteTask(item.id)}
                                sx={getIAStyles('iconOnly')}>
                                <DeleteIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </>
                    ) : (
                      <ListItem>
                        <ListItemText
                          primary={
                            "This project doesn't have must haves, you can add one using the menu in the top right corner"
                          }></ListItemText>
                      </ListItem>
                    )}
                    {provided.placeholder}
                  </List>
                )}
              </Droppable>
              <Box margin={1}>
                <Divider> Nice to have </Divider>
              </Box>
              <Droppable droppableId={`nice_to_have_${id}`}>
                {provided => (
                  <List
                    sx={{ overflow: 'auto', height: '150px' }}
                    ref={provided.innerRef}
                    {...provided.droppableProps}>
                    {(taskLists.nice_to_have.length > 0 || local_ns.length > 0) ? (
                      <>
                        {taskLists.nice_to_have.map((item, index) => {
                          const labelId = `checkbox-list-label-${item.id}`;
                          const dragid = `draggable-${item.id}`;
                          return (
                            <Draggable
                              key={dragid}
                              draggableId={dragid}
                              index={index}
                              isDragDisabled={(role !== 'Member' && !admin) || is_suggestion}>
                              {provided => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}>
                                  <ListItem>
                                    <BootstrapTooltip title={item.description} placement="top">
                                      <ListItemButton
                                        disableRipple
                                        dense
                                        onClick={is_suggestion ? undefined : handleToggle(item)}
                                        sx={{ cursor: is_suggestion ? 'default' : 'pointer' }}>
                                        {!is_suggestion && (
                                          <Checkbox
                                            edge="start"
                                            disableRipple
                                            checked={Boolean(item.is_completed)}
                                            disabled={is_suggestion}
                                            inputProps={{
                                              'aria-labelledby': labelId,
                                            }}
                                          />
                                        )}
                                        <ListItemText primary={item.name} />
                                      </ListItemButton>
                                    </BootstrapTooltip>
                                    {deleteMode || is_suggestion ? (
                                      <ListItemSecondaryAction>
                                        <IconButton
                                          edge='end'
                                          onClick={() => { setTask(item); }}>
                                          <EditIcon />
                                        </IconButton>
                                        <IconButton
                                          edge='end'
                                          onClick={() => is_suggestion
                                            ? handleDeleteTask(item.id)
                                            : setToDelete(item.id)}>
                                          <DeleteIcon />
                                        </IconButton>
                                      </ListItemSecondaryAction>
                                    ) : null}
                                  </ListItem>
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {local_ns.map((item) => (
                          <ListItem key={item.id} sx={getIAStyles('suggestionItem')}>
                            <BootstrapTooltip title={item.description} placement="top">
                              <ListItemButton
                                disableRipple
                                dense
                                sx={{ cursor: 'default' }}>
                                <ListItemText
                                  primary={<span className="ai-suggestion-text">{item.name}</span>}
                                  primaryTypographyProps={{ style: { color: '#9C27B0' } }}
                                />
                              </ListItemButton>
                            </BootstrapTooltip>
                            <ListItemSecondaryAction>
                              <IconButton
                                edge='end'
                                onClick={() => { setTask(item); }}
                                sx={getIAStyles('iconOnly')}>
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                edge='end'
                                onClick={() => handleDeleteTask(item.id)}
                                sx={getIAStyles('iconOnly')}>
                                <DeleteIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </>
                    ) : (
                      <ListItem>
                        <ListItemText
                          sx={{ textAlign: 'center' }}
                          primary={'No tasks'}></ListItemText>
                      </ListItem>
                    )}
                    {provided.placeholder}
                  </List>
                )}
              </Droppable>
            </Box>
          ) : props.noMargin || props.isDragging ? (
            <Typography
              variant="subtitle2"
              component="div"
              align="center"
              marginTop={'1em'}>
              {total_tasks} tasks inside
            </Typography>
          ) : (
            <></>
          )}
        </CardContent>
      </Card>
      <AddTaskDialog
        scope={scope}
        open={open}
        close={handleOpen}
        closeMenu={handleClose}
        scope_id={id}
        created_by={created_by}
        project={project}
        users={users}
        user_id={user_id}
      />
      <EditTaskDialog
        scope={scope}
        open={openEditTask}
        close={handleCloseTaskDialog}
        closeMenu={handleClose}
        scope_id={id}
        created_by={created_by}
        project={project}
        users={users}
        user_id={user_id}
        task={task}
        setTask={setTask}
        onLocalSave={is_suggestion ? handleLocalEditTaskSave : (task.is_suggestion ? handleLocalSuggestionSave : undefined)}
      />
      <DeleteDialog
        open={openDeleteDialog}
        toDelete={id}
        toDeleteName={'scope'}
        deleteFunction={deleteScope}
        getFunction={getScopes}
        optionalGetId={project_id}
        loadingUpdate={loadingUpdate}
        getSecondary={getProjects}
        closeDialog={setOpenDeleteDialog}
        goHome={false}
        closeMenu={handleClose}
      />

      <DeleteDialog
        open={openDeleteTask}
        toDelete={toDelete}
        toDeleteName={'task'}
        deleteFunction={handleDeleteTask}
        getFunction={undefined}
        optionalGetId={project_id}
        loadingUpdate={loadingUpdate}
        getSecondary={is_suggestion ? undefined : getProjects}
        closeDialog={setOpenDeleteTask}
        goHome={false}
        closeMenu={handleCloseDeleteTask}
      />
      <EditScopeDialog
        open={openEditScopeDialog}
        closeDialog={handleCloseScopeDialog}
        scope={scope}
        project_id={project_id}
        closeMenu={handleClose}
        key={scope.id + scope.name + scope.description + scope.color}
        onLocalSave={is_suggestion ? handleLocalEditScopeSave : undefined}
      />
    </Paper>
  );
}
